import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { safeApiError } from "@/lib/safeErrorText";
import {
  ShieldCheck, ServerCog, HardDrive, Database, KeyRound, Trash2, RefreshCw,
  Loader2, Sparkles, Disc3, Mic2, Folder, Clock, ArrowLeft, Lock, AlertTriangle,
  CheckCircle2, XCircle, PlayCircle, Hourglass,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
 *  /admin — operator one-page dashboard
 *
 *  Surfaces everything an operator needs to keep the disk + DB healthy:
 *   - Live storage footprint across all four /app domains
 *   - Mongo collection counts
 *   - Per-share row with size + plays + delete (uuid-as-bearer)
 *   - Big "Run Janitor Sweep" button (returns the sweep diff)
 *   - Health card (Mongo + integration-key presence)
 *
 *  Auth: X-Admin-Token header. Token is entered once and persisted to
 *  localStorage so refreshes don't require re-pasting. Token NEVER leaves
 *  the browser except in the request header.
 *  ───────────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "elpm-admin-token";

function bytesToHuman(b) {
  if (!Number.isFinite(b) || b <= 0) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return `${Math.round(diff / (86400 * 30))}mo ago`;
  } catch (e) { void e; return "—"; }
}

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-50">
      <div className="absolute -left-40 top-0 h-[460px] w-[460px] rounded-full bg-emerald-500/[0.18] blur-3xl" />
      <div className="absolute right-[-220px] top-40 h-[540px] w-[540px] rounded-full bg-sky-500/[0.16] blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-amber-500/[0.10] blur-3xl" />
    </div>
  );
}

const DOMAIN_LABELS = {
  session_shares:       { icon: Disc3,  label: "Session shares",       hint: "Public master-bus WAV bounces" },
  session_share_covers: { icon: Folder, label: "Session cover art",    hint: "User-uploaded JPG/PNG/WEBP" },
  recordings:           { icon: Folder, label: "Recordings",           hint: "Streamed-to-disk MediaRecorder chunks" },
  avatars:              { icon: Mic2,   label: "Voice avatars",        hint: "Per-voice clone photo" },
};

/** Token gate — shown until the user pastes a valid admin token. */
function TokenGate({ onSubmit }) {
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e?.preventDefault?.();
    if (!token.trim()) {
      setErr("Paste a token first");
      return;
    }
    onSubmit(token.trim());
  };
  return (
    <div className="relative z-10 mx-auto max-w-md p-6 pt-[18vh]" data-testid="admin-gate">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 shadow-2xl backdrop-blur-md"
      >
        <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
          <Lock className="h-3.5 w-3.5" />Restricted · Operator
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="mt-2 font-body text-[13.5px] leading-6 text-white/65">
          Paste your <code className="rounded bg-white/10 px-1 text-fuchsia-200">ADMIN_TOKEN</code>{" "}
          to view storage and run the janitor sweep. The token is stored in this
          browser only.
        </p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
          <input
            type="password"
            autoFocus
            data-testid="admin-token-input"
            value={token}
            onChange={(e) => { setToken(e.target.value); setErr(""); }}
            placeholder="Admin token"
            className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-[13px] text-white outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/30"
          />
          {err ? (
            <p className="font-mono text-[11px] text-rose-300" data-testid="admin-token-err">{err}</p>
          ) : null}
          <button
            type="submit"
            data-testid="admin-token-submit"
            className="rounded-2xl bg-gradient-to-r from-emerald-500/85 to-sky-500/85 px-5 py-3 font-semibold text-white shadow-[0_12px_36px_-12px_rgba(16,185,129,0.6)] transition hover:from-emerald-500 hover:to-sky-500"
          >
            Unlock dashboard
          </button>
        </form>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-white/40 transition hover:text-white/70"
        >
          <ArrowLeft className="h-3 w-3" /> back to studio
        </Link>
      </motion.div>
    </div>
  );
}

function StatCard({ icon: Icon, label, hint, value, sub, accent = "fuchsia", testId }) {
  const accentMap = {
    fuchsia: "from-fuchsia-500/25 to-fuchsia-500/5 border-fuchsia-400/30 text-fuchsia-200",
    sky:     "from-sky-500/25     to-sky-500/5     border-sky-400/30     text-sky-200",
    emerald: "from-emerald-500/25 to-emerald-500/5 border-emerald-400/30 text-emerald-200",
    amber:   "from-amber-500/25   to-amber-500/5   border-amber-400/30   text-amber-200",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-3xl border bg-gradient-to-br p-5 shadow-[0_18px_42px_-26px_rgba(0,0,0,0.55)] backdrop-blur ${accentMap[accent]}`}
    >
      <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em]">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <p className="font-display text-3xl font-bold leading-none tracking-tight text-white">
        {value}
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
        {sub || hint}
      </p>
    </div>
  );
}

function StorageRow({ name, info, totalBytes }) {
  const meta = DOMAIN_LABELS[name] || { icon: Folder, label: name, hint: "" };
  const Icon = meta.icon;
  const pct = totalBytes > 0 ? Math.min(100, (info.bytes / totalBytes) * 100) : 0;
  return (
    <div
      data-testid={`admin-storage-${name}`}
      className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-white/65" />
          <div>
            <p className="font-display text-[14px] font-semibold leading-none text-white">{meta.label}</p>
            <p className="mt-1 font-mono text-[9.5px] uppercase tracking-wider text-white/40">{info.path}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono text-[13px] font-semibold tabular-nums text-white">
            {bytesToHuman(info.bytes)}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
            {info.files} file{info.files === 1 ? "" : "s"}
          </p>
        </div>
      </div>
      <div className="relative h-1.5 overflow-hidden rounded-full bg-white/[0.05]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-fuchsia-500/85 via-sky-500/85 to-emerald-500/85 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function HealthRow({ ok, label, sub, latencyMs }) {
  // iter-115 — when latencyMs is provided, show it as a coloured chip next
  // to the OK/Missing label so admins can spot degraded-but-working
  // integrations (e.g. Suno responds but takes 4 s). Thresholds chosen to
  // match SLO buckets: < 500 ms = fast, < 2 s = ok, > 2 s = slow.
  const latColor = latencyMs == null
    ? null
    : latencyMs < 500 ? "text-emerald-300"
    : latencyMs < 2000 ? "text-amber-300"
    : "text-rose-300";
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-300" />
      ) : (
        <XCircle className="h-4 w-4 flex-shrink-0 text-rose-300" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-wider text-white">{label}</p>
        {sub ? <p className="font-mono text-[9.5px] text-white/50 truncate">{sub}</p> : null}
      </div>
      {latColor != null && (
        <span data-testid={`admin-health-latency-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`font-mono text-[10px] tabular-nums ${latColor}`}>
          {latencyMs}ms
        </span>
      )}
      <span
        className={`font-mono text-[10px] uppercase tracking-wider ${
          ok ? "text-emerald-200" : "text-rose-200"
        }`}
      >
        {ok ? "OK" : "Missing"}
      </span>
    </div>
  );
}

function LargestSharesTable({ rows, onDelete, deleting }) {
  if (!rows.length) {
    return (
      <div
        data-testid="admin-shares-empty"
        className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center"
      >
        <Sparkles className="mx-auto mb-2 h-6 w-6 text-white/30" />
        <p className="font-display text-[14px] text-white/65">No shared sessions on disk yet</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.02]">
      <table className="w-full text-left font-body text-[13px]" data-testid="admin-shares-table">
        <thead className="bg-white/[0.04] font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
          <tr>
            <th className="px-3 py-2">Title</th>
            <th className="px-3 py-2 text-right">Size</th>
            <th className="px-3 py-2 text-right">Plays</th>
            <th className="px-3 py-2">Age</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-share-row-${r.id}`}
              className="border-t border-white/[0.06] transition hover:bg-white/[0.025]"
            >
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Disc3 className={`h-3.5 w-3.5 flex-shrink-0 ${r.has_file ? "text-fuchsia-300" : "text-rose-300"}`} />
                  <div className="min-w-0">
                    <p className="line-clamp-1 font-display text-[13px] font-medium text-white">{r.title}</p>
                    <p className="line-clamp-1 font-mono text-[9.5px] text-white/40">
                      {r.owner_alias || "anon"} · {r.id.slice(0, 8)}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-white/85">
                {bytesToHuman(r.bytes)}
              </td>
              <td className="px-3 py-2.5 text-right font-mono text-[12px] tabular-nums text-white/85">
                {r.plays.toLocaleString()}
              </td>
              <td className="px-3 py-2.5 font-mono text-[11px] text-white/60">{timeAgo(r.created_at)}</td>
              <td className="px-3 py-2.5 text-right">
                <div className="inline-flex items-center gap-1.5">
                  <Link
                    to={`/session/${r.id}`}
                    data-testid={`admin-share-view-${r.id}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open public viewer"
                    className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white/65 transition hover:bg-white/[0.1]"
                  >
                    <PlayCircle className="inline h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    data-testid={`admin-share-delete-${r.id}`}
                    onClick={() => onDelete(r)}
                    disabled={deleting === r.id}
                    title="Delete share"
                    className="rounded-lg border border-rose-400/30 bg-rose-500/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-rose-200 transition hover:bg-rose-500/30 disabled:opacity-50"
                  >
                    {deleting === r.id ? (
                      <Loader2 className="inline h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="inline h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (e) { void e; return ""; }
  });
  const [stats, setStats] = useState(null);
  // iter-115 — live integration latency probes. Fetched alongside stats
  // so each integration row shows REAL response times + actionable detail
  // (e.g. "Suno · 210ms · credits: 16307" vs just "configured").
  const [probes, setProbes] = useState(null);
  const [probesLoading, setProbesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // URL ?token=… one-shot bootstrap: lets ops paste a deep link in chat.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
  // iter-156 — memo the cross-collection aggregate so we don't reduce
  // 30+ values on every render. Stats is updated only via setStats so
  // a stable reference is fine for the dependency.
  const mongoTotals = useMemo(() => {
    const coll = stats?.collections || {};
    const total = Object.values(coll).reduce((s, v) => s + Math.max(0, v), 0);
    return {
      totalDisplay: total.toLocaleString(),
      collectionCount: Object.keys(coll).length,
    };
  }, [stats?.collections]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/session/share/admin/stats`, { headers });
      setStats(res.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 503) {
        toast.error("Invalid admin token. Re-enter the token from .env.");
        try { localStorage.removeItem(TOKEN_KEY); } catch (er) { void er; }
        setToken("");
        setStats(null);
      } else {
        setErr(safeApiError(e, "Failed to load"));
        toast.error("Failed to load admin stats");
      }
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  const fetchProbes = useCallback(async () => {
    if (!token) return;
    setProbesLoading(true);
    try {
      const res = await axios.get(`${API}/session/share/admin/probes`, { headers, timeout: 15000 });
      setProbes(res.data);
    } catch (e) {
      console.warn("[admin·probes]", safeApiError(e));
      setProbes(null);
    } finally {
      setProbesLoading(false);
    }
  }, [token, headers]);

  useEffect(() => {
    // Pull token from URL on first mount if present (deep-link convenience).
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get("token");
      if (q && !tokenRef.current) {
        localStorage.setItem(TOKEN_KEY, q);
        setToken(q);
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) { void e; }
  }, []);

  useEffect(() => {
    if (token) { fetchStats(); fetchProbes(); }
  }, [token, fetchStats, fetchProbes]);

  const handleTokenSubmit = (t) => {
    try { localStorage.setItem(TOKEN_KEY, t); } catch (e) { void e; }
    setToken(t);
  };

  const handleLogout = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch (e) { void e; }
    setToken("");
    setStats(null);
    toast.success("Logged out of admin");
  };

  const handleSweep = async () => {
    if (!token || sweeping) return;
    setSweeping(true);
    setSweepResult(null);
    try {
      const res = await axios.post(`${API}/session/share/admin/sweep`, null, { headers });
      setSweepResult(res.data);
      toast.success(`Sweep complete — ${res.data.records_deleted} records, ${res.data.orphans_deleted} orphans, ${bytesToHuman(res.data.bytes_freed)} freed`);
      fetchStats();
    } catch (e) {
      toast.error(safeApiError(e, "Sweep failed"));
    } finally {
      setSweeping(false);
    }
  };

  const handleDeleteShare = async (row) => {
    if (!token || deleting) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${row.title}"? This frees ${bytesToHuman(row.bytes)} and breaks any existing share link.`)) return;
    setDeleting(row.id);
    try {
      await axios.delete(`${API}/session/share/${row.id}`);
      toast.success(`Deleted "${row.title}" · ${bytesToHuman(row.bytes)} freed`);
      fetchStats();
    } catch (e) {
      toast.error(safeApiError(e, "Delete failed"));
    } finally {
      setDeleting(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen overflow-x-hidden font-body text-white">
        <title>Admin · ELPM Studio Pro</title>
        <AmbientBackdrop />
        <TokenGate onSubmit={handleTokenSubmit} />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden font-body text-white" data-testid="admin-dashboard">
      <title>Admin · ELPM Studio Pro</title>
      <AmbientBackdrop />

      <div className="relative z-10 mx-auto max-w-6xl p-4 md:p-8">
        {/* ─── Header ─── */}
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="admin-home-link"
            className="group flex items-center gap-3 transition hover:opacity-90"
          >
            <img
              src="/brand/elpm-logo.png"
              alt="ELPM Studio Pro"
              data-testid="brand-wordmark-admin"
              className="h-9 w-auto select-none drop-shadow-[0_2px_14px_rgba(16,185,129,0.2)]"
              draggable="false"
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition group-hover:text-emerald-200">
              ← Back to studio
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-refresh-btn"
              onClick={() => { fetchStats(); fetchProbes(); }}
              disabled={loading || probesLoading}
              className="studio-pill rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/65 transition hover:border-white/20 hover:bg-white/[0.08] disabled:opacity-50"
            >
              {(loading || probesLoading) ? (
                <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 inline h-3.5 w-3.5" />
              )}
              Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="studio-pill rounded-2xl border border-rose-400/25 bg-rose-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-rose-200 transition hover:bg-rose-500/20"
            >
              Lock
            </button>
          </div>
        </header>

        {/* ─── Title row ─── */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card mb-6 rounded-3xl p-6"
        >
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-300">
            <ShieldCheck className="h-3.5 w-3.5" />Operator · Console
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
            Storage & Janitor Dashboard
          </h1>
          <p className="mt-2 max-w-2xl font-body text-[14px] leading-6 text-white/65">
            Disk footprint across every session share, voice avatar, and streamed
            recording. Sweep manually whenever the disk is getting tight — the
            background janitor runs every {Math.round((stats?.janitor?.interval_seconds || 3600) / 60)} min
            with a {stats?.janitor?.ttl_days ?? "—"}-day TTL.
          </p>
        </motion.section>

        {err ? (
          <div
            data-testid="admin-error-banner"
            className="mb-6 flex items-center gap-3 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 font-mono text-[12px] text-rose-200"
          >
            <AlertTriangle className="h-4 w-4" />
            Could not load stats — {err}
          </div>
        ) : null}

        {/* ─── Top-line cards ─── */}
        <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            icon={HardDrive}
            label="Disk used"
            value={bytesToHuman(stats?.total_bytes || 0)}
            sub={`${stats?.total_files ?? 0} files on /app`}
            accent="fuchsia"
            testId="admin-stat-disk"
          />
          <StatCard
            icon={Database}
            label="Mongo records"
            value={mongoTotals.totalDisplay}
            sub={`${mongoTotals.collectionCount} collections`}
            accent="sky"
            testId="admin-stat-mongo"
          />
          <StatCard
            icon={Clock}
            label="Janitor TTL"
            value={`${stats?.janitor?.ttl_days ?? "—"}d`}
            sub={`Every ${Math.round((stats?.janitor?.interval_seconds || 3600) / 60)} min`}
            accent="amber"
            testId="admin-stat-ttl"
          />
          <StatCard
            icon={ServerCog}
            label="Mongo"
            value={stats?.health?.mongo_ok ? "Online" : "Offline"}
            sub={stats?.health?.mongo_ok ? "Ping OK" : "Connection failed"}
            accent={stats?.health?.mongo_ok ? "emerald" : "amber"}
            testId="admin-stat-mongo-health"
          />
        </section>

        {/* ─── Storage breakdown ─── */}
        <section className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="admin-storage-grid">
          {stats?.storage
            ? Object.entries(stats.storage).map(([name, info]) => (
                <StorageRow key={name} name={name} info={info} totalBytes={stats.total_bytes} />
              ))
            : null}
        </section>

        {/* ─── Janitor + sweep ─── */}
        <section className="mb-6 rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6" data-testid="admin-sweep-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
                <Hourglass className="h-3.5 w-3.5" />Janitor · Manual Trigger
              </div>
              <p className="font-display text-[16px] font-semibold text-white">Run janitor sweep</p>
              <p className="mt-1 font-mono text-[11px] text-white/55">
                Drops every share older than {stats?.janitor?.ttl_days ?? "—"} days and any orphan files
                with no matching Mongo record.
              </p>
            </div>
            <button
              type="button"
              data-testid="admin-sweep-btn"
              onClick={handleSweep}
              disabled={sweeping}
              className="rounded-2xl bg-gradient-to-r from-emerald-500/85 to-sky-500/85 px-5 py-2.5 font-semibold text-white shadow-[0_12px_36px_-12px_rgba(16,185,129,0.6)] transition hover:from-emerald-500 hover:to-sky-500 disabled:opacity-60"
            >
              {sweeping ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 inline h-4 w-4" />
              )}
              {sweeping ? "Sweeping…" : "Run sweep now"}
            </button>
          </div>
          {sweepResult ? (
            <div
              data-testid="admin-sweep-result"
              className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4"
            >
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">Records evicted</p>
                <p className="mt-1 font-display text-2xl font-bold text-white">{sweepResult.records_deleted}</p>
              </div>
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">Orphan files</p>
                <p className="mt-1 font-display text-2xl font-bold text-white">{sweepResult.orphans_deleted}</p>
              </div>
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">Bytes freed</p>
                <p className="mt-1 font-display text-2xl font-bold text-emerald-200">{bytesToHuman(sweepResult.bytes_freed)}</p>
              </div>
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">Duration</p>
                <p className="mt-1 font-display text-2xl font-bold text-white">{sweepResult.duration_ms} ms</p>
              </div>
            </div>
          ) : null}
        </section>

        {/* ─── Two-col: largest shares + Mongo + integrations ─── */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          <div>
            <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
              <Disc3 className="h-3.5 w-3.5" />Top 10 · Largest Session Shares
            </div>
            <LargestSharesTable
              rows={stats?.largest_shares || []}
              onDelete={handleDeleteShare}
              deleting={deleting}
            />
          </div>

          <div className="space-y-4">
            <div>
              <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                <Database className="h-3.5 w-3.5" />Mongo · Collections
              </div>
              <div
                data-testid="admin-collections-list"
                className="space-y-1.5 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-3"
              >
                {stats?.collections
                  ? Object.entries(stats.collections).map(([k, v]) => (
                      <div
                        key={k}
                        data-testid={`admin-coll-${k}`}
                        className="flex items-center justify-between rounded-lg px-2 py-1.5 transition hover:bg-white/[0.025]"
                      >
                        <span className="font-mono text-[11px] text-white/75">{k}</span>
                        <span className="font-mono text-[11px] font-semibold tabular-nums text-white">
                          {v < 0 ? "n/a" : v.toLocaleString()}
                        </span>
                      </div>
                    ))
                  : <p className="font-mono text-[10px] text-white/40">Loading…</p>}
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                <span className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" />Integration · Health</span>
                {probesLoading && <Loader2 className="h-3 w-3 animate-spin text-white/45" />}
              </div>
              <div className="space-y-2" data-testid="admin-keys-list">
                {/* iter-115 — show LIVE probe results (latency + detail) for
                    every integration that has a probe. Falls back to the
                    boolean "configured" view for env-var-only entries
                    (ADMIN_TOKEN, OWNER_EMAILS) that don't have an upstream
                    to probe. */}
                {(probes?.results || []).map((p) => (
                  <HealthRow
                    key={p.id}
                    ok={p.ok}
                    label={p.label}
                    sub={p.detail || (p.ok ? "ok" : "failed")}
                    latencyMs={p.skipped ? null : p.latency_ms}
                  />
                ))}
                {/* Boolean-only flags — no probe, just env presence. */}
                {stats?.health?.integrations
                  ? Object.entries(stats.health.integrations)
                      .filter(([k]) => k === "ADMIN_TOKEN" || k === "OWNER_EMAILS")
                      .map(([k, ok]) => (
                        <HealthRow key={k} ok={ok} label={k} sub={ok ? "configured" : "missing — see /app/backend/.env"} />
                      ))
                  : null}
                {!probes && !probesLoading && (
                  <p className="font-mono text-[10px] text-white/40">No probe data yet — hit Refresh.</p>
                )}
              </div>
              {probes?.summary && (
                <p className="mt-2 font-mono text-[9.5px] uppercase tracking-wider text-white/40">
                  Probe summary · {probes.summary.ok}/{probes.summary.total} OK
                  {probes.summary.failed > 0 ? ` · ${probes.summary.failed} failing` : ""}
                </p>
              )}
            </div>
          </div>
        </section>

        <footer className="mt-10 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-white/45">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" />
            Snapshot · {stats?.generated_at ? new Date(stats.generated_at).toLocaleString() : "—"}
          </span>
          <Link to="/" className="hover:text-white/70">Open studio →</Link>
        </footer>
      </div>
    </div>
  );
}
